/**
 * /invite — where someone with no invite ends up: the app's "Request an invite" and the beta
 * landing's "Join the waitlist" both point here. It is the city waitlist with the closed beta said
 * out loud: what they are asking for, what happens after, and the two shortcuts (an approval email
 * they already have, a friend already inside).
 *
 * The request is the same POST /waitlist/full-release row as the homepage form, tagged
 * source=invite_request and carrying the metro, which is what the founder bridges into the beta by.
 */
import { useCallback, useState, type FormEvent } from 'react';

import { fullReleaseWaitlist } from '~/marketing/landingContent';
import { MarketingSiteLayout } from '../components/marketing/MarketingSiteLayout';
import { captureEvent, presenceEvents } from '../presence/posthogPresence';
import {
  fullReleaseWaitlistEnabled,
  submitFullReleaseWaitlist,
} from '../waitlist/fullReleaseWaitlist';
import '../components/marketing/landingRef.css';
import './invitePage.css';

export const INVITE_REQUEST_SOURCE = 'invite_request';

export const INVITE_CITY_OPTIONS = [
  { value: 'austin', label: 'Austin, TX' },
  { value: 'other', label: 'Somewhere else' },
] as const;

type InviteCity = (typeof INVITE_CITY_OPTIONS)[number]['value'];

export const inviteCopy = {
  kicker: 'Closed beta',
  title: 'Request an invite',
  lead: "Kynward is in a closed beta, starting in Austin. Leave the email you'll sign up with and we'll send an invite link when there's a seat for you.",
  emailLabel: 'Email',
  cityLabel: 'Your city',
  cityOtherLabel: 'Which city?',
  submit: 'Request an invite',
  successNew:
    "You're on the list. We'll email your invite link when there's a seat. Austin is open now, so for Austin that can be soon.",
  successAgain: "You're already on the list. We'll email you when there's a seat.",
  errorGeneric: "That didn't go through. Try again in a moment.",
  nextTitle: 'What happens next',
  next: [
    'We email you an invite link.',
    'Open it, read the beta agreement, and accept it.',
    'Install Kynward and create your account with this same email. The app checks it against your invite.',
  ],
  invitedTitle: 'Already invited?',
  invitedBody:
    'Open the link in your approval email. It takes you to the agreement and the install steps.',
  friendTitle: 'Know someone in the beta?',
  friendBody: "Their invite link gets you in without waiting. Open it on the phone you'll use.",
} as const;

function InviteRequestForm() {
  const enabled = fullReleaseWaitlistEnabled();
  const [email, setEmail] = useState('');
  const [city, setCity] = useState<InviteCity>('austin');
  const [cityOther, setCityOther] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!enabled || status === 'loading') return;
      setStatus('loading');
      setMessage('');
      const result = await submitFullReleaseWaitlist({
        email,
        citySlug: city,
        cityOther: city === 'other' ? cityOther : undefined,
        source: INVITE_REQUEST_SOURCE,
      });
      if (!result.ok) {
        setStatus('error');
        setMessage(result.message);
        return;
      }
      captureEvent(presenceEvents.waitlist_full_release_submit, {
        created: result.data.created,
        already_registered: result.data.alreadyRegistered,
        source: INVITE_REQUEST_SOURCE,
      });
      setStatus('success');
      setMessage(result.data.alreadyRegistered ? inviteCopy.successAgain : inviteCopy.successNew);
    },
    [city, cityOther, email, enabled, status]
  );

  if (!enabled) {
    return (
      <p className="invite-note ml-text-body-secondary" role="status">
        {fullReleaseWaitlist.disabledHint}
      </p>
    );
  }

  if (status === 'success') {
    return (
      <p className="invite-success" role="status">
        {message}
      </p>
    );
  }

  return (
    <form className="invite-form" onSubmit={onSubmit} noValidate>
      <label className="invite-label" htmlFor="invite-email">
        {inviteCopy.emailLabel}
      </label>
      <input
        id="invite-email"
        className="hero-email-capture__input invite-input"
        type="email"
        name="email"
        autoComplete="email"
        placeholder="you@example.com"
        required
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
      />
      <label className="invite-label" htmlFor="invite-city">
        {inviteCopy.cityLabel}
      </label>
      <select
        id="invite-city"
        className="invite-select"
        value={city}
        onChange={(ev) => setCity(ev.target.value as InviteCity)}>
        {INVITE_CITY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {city === 'other' ? (
        <>
          <label className="invite-label" htmlFor="invite-city-other">
            {inviteCopy.cityOtherLabel}
          </label>
          <input
            id="invite-city-other"
            className="hero-email-capture__input invite-input"
            type="text"
            name="cityOther"
            autoComplete="address-level2"
            placeholder="City, state"
            value={cityOther}
            onChange={(ev) => setCityOther(ev.target.value)}
          />
        </>
      ) : null}
      <button
        type="submit"
        className="ml-btn ml-btn--primary eb-cta eb-cta--primary invite-submit"
        disabled={status === 'loading'}
        aria-busy={status === 'loading'}>
        {status === 'loading' ? (
          <span className="ml-waitlistForm__spinner" role="status" aria-label="Sending" />
        ) : (
          inviteCopy.submit
        )}
      </button>
      {status === 'error' ? (
        <p className="hero-email-capture__err" role="alert">
          {message || inviteCopy.errorGeneric}
        </p>
      ) : null}
    </form>
  );
}

export function InvitePage() {
  return (
    <MarketingSiteLayout>
      <article className="invite-page">
        <header className="invite-hero">
          <p className="invite-kicker">{inviteCopy.kicker}</p>
          <h1 className="invite-h1">{inviteCopy.title}</h1>
          <p className="invite-lead">{inviteCopy.lead}</p>
          <InviteRequestForm />
        </header>

        <div className="invite-content">
          <section className="invite-section" aria-labelledby="invite-next">
            <h2 id="invite-next" className="invite-h2">
              {inviteCopy.nextTitle}
            </h2>
            <ol className="invite-steps">
              {inviteCopy.next.map((step) => (
                <li key={step} className="invite-step">
                  {step}
                </li>
              ))}
            </ol>
          </section>

          <section className="invite-section" aria-labelledby="invite-invited">
            <h2 id="invite-invited" className="invite-h2">
              {inviteCopy.invitedTitle}
            </h2>
            <p className="invite-p">{inviteCopy.invitedBody}</p>
          </section>

          <section className="invite-section" aria-labelledby="invite-friend">
            <h2 id="invite-friend" className="invite-h2">
              {inviteCopy.friendTitle}
            </h2>
            <p className="invite-p">{inviteCopy.friendBody}</p>
          </section>
        </div>
      </article>
    </MarketingSiteLayout>
  );
}
